import { Component, computed, input, output } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import type { ChartType } from 'chart.js';

import { WidgetChart, WidgetRepresentation } from './dashboard-widget.model';
import { CardHeaderComponent } from './card-header.component';
import { RepSwitcherComponent } from './rep-switcher.component';

/**
 * Renders a chart widget with PrimeNG's `p-chart` (Chart.js). One card frame
 * with a title/subtitle header, then the plot. Maps our neutral `WidgetChart`
 * payload + a representation type to a Chart.js `{ data, options }`.
 *
 * Colour policy (dataviz): a single-series magnitude bar/line uses ONE hue;
 * part-to-whole (donut) and multi-series (radar/line) draw the categorical
 * palette in FIXED order — never cycled, never rank-based. Text/axis stay
 * recessive grey so identity lives in the marks, not the labels.
 */
@Component({
  selector: 'app-chart-tile',
  standalone: true,
  imports: [ChartModule, CardHeaderComponent, RepSwitcherComponent],
  template: `
    <div
      class="card flex h-full min-h-[24rem] flex-col gap-3 rounded-xl border border-surface-200/60 bg-surface-0 p-5 dark:border-surface-700/40 dark:bg-surface-900"
    >
      <app-card-header [title]="title()" [subtitle]="subtitle()" [icon]="icon()" [accent]="accent()">
        <div header-actions class="flex items-center gap-2">
          @if (badge()) {
          <span
            class="rounded-full bg-surface-100 px-2 py-0.5 text-xs font-medium text-text-muted dark:bg-surface-800"
            >{{ badge() }}</span
          >
          }
          <app-rep-switcher [reps]="reps()" [active]="type()" (repChange)="repChange.emit($event)" />
        </div>
      </app-card-header>

      @if (loading()) {
      <div class="w-full flex-1 animate-pulse rounded-lg bg-surface-100 dark:bg-surface-800"></div>
      } @else if (chart()) {
      <div class="min-h-0 flex-1">
        <p-chart [type]="chartJsType()" [data]="data()" [options]="options()" [style]="{ height: '80%' }" />
      </div>
      } @else {
      <div class="flex flex-1 items-center justify-center text-sm text-text-muted">—</div>
      }
    </div>
  `,
})
export class ChartTileComponent {
  title = input.required<string>();
  subtitle = input<string>('');
  icon = input<string>('');
  accent = input<string>('');
  /** Small text badge on the header, e.g. the summary value. */
  badge = input<string>('');
  type = input.required<WidgetRepresentation>();
  chart = input<WidgetChart | undefined>(undefined);
  loading = input<boolean>(false);
  /** Representations offered in the header switcher (shown only if > 1). */
  reps = input<WidgetRepresentation[]>([]);
  /** Fires when the user picks a different representation. */
  repChange = output<WidgetRepresentation>();

  /** Categorical hues in FIXED order — assigned by index, never cycled past 7. */
  private static readonly PALETTE = [
    '#3b82f6', // blue
    '#f59e0b', // amber
    '#14b8a6', // teal
    '#ef4444', // red
    '#8b5cf6', // violet
    '#22c55e', // green
    '#64748b', // slate
  ];
  private static readonly GRID = 'rgba(148, 163, 184, 0.18)';
  private static readonly INK = '#94a3b8';

  chartJsType = computed<ChartType>(() =>
    this.type() === 'donut' ? 'doughnut' : (this.type() as ChartType),
  );

  data = computed(() => {
    const c = this.chart();
    if (!c) return { labels: [], datasets: [] };
    const type = this.type();

    // Part-to-whole (donut & pie): one dataset, one colour per slice.
    if (type === 'donut' || type === 'pie') {
      return {
        labels: c.labels,
        datasets: [
          {
            data: c.datasets[0]?.data ?? [],
            backgroundColor: c.labels.map((_, i) => ChartTileComponent.PALETTE[i % 7]),
            borderColor: '#ffffff',
            borderWidth: 2,
          },
        ],
      };
    }

    // Radar: each series a palette hue with a translucent fill.
    if (type === 'radar') {
      return {
        labels: c.labels,
        datasets: c.datasets.map((d, i) => {
          const hue = ChartTileComponent.PALETTE[i % 7];
          return {
            label: d.label,
            data: d.data,
            borderColor: hue,
            backgroundColor: hue + '22',
            pointBackgroundColor: hue,
            borderWidth: 2,
          };
        }),
      };
    }

    // bar / line. Single series → one hue; multi (actual+forecast) → palette order.
    const single = c.datasets.length === 1;
    return {
      labels: c.labels,
      datasets: c.datasets.map((d, i) => {
        const hue = single ? ChartTileComponent.PALETTE[0] : ChartTileComponent.PALETTE[i % 7];
        const asLine = (d.kind ?? type) === 'line';
        return {
          type: d.kind ?? (type === 'line' ? 'line' : 'bar'),
          label: d.label,
          data: d.data,
          borderColor: hue,
          backgroundColor: asLine ? hue + '22' : hue,
          borderWidth: asLine ? 2 : 0,
          borderRadius: asLine ? 0 : 4,
          borderDash: d.dashed ? [5, 4] : undefined,
          fill: asLine && single,
          tension: 0.35,
          pointRadius: asLine ? 3 : 0,
          spanGaps: false,
        };
      }),
    };
  });

  options = computed(() => {
    const type = this.type();
    const partToWhole = type === 'donut' || type === 'pie';
    const showLegend = partToWhole || type === 'radar' || (this.chart()?.datasets.length ?? 0) > 1;

    const base: any = {
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: {
          display: showLegend,
          position: partToWhole ? 'right' : 'top',
          labels: { color: ChartTileComponent.INK, usePointStyle: true, boxWidth: 8, font: { size: 11 } },
        },
        tooltip: { enabled: true },
      },
    };

    if (partToWhole) return base;

    if (type === 'radar') {
      return {
        ...base,
        scales: {
          r: {
            angleLines: { color: ChartTileComponent.GRID },
            grid: { color: ChartTileComponent.GRID },
            pointLabels: { color: ChartTileComponent.INK, font: { size: 10 } },
            ticks: { display: false },
            suggestedMin: 0,
            suggestedMax: 100,
          },
        },
      };
    }

    // bar / line — one y-axis, recessive grid.
    return {
      ...base,
      scales: {
        x: { grid: { display: false }, ticks: { color: ChartTileComponent.INK, font: { size: 11 } } },
        y: {
          beginAtZero: true,
          grid: { color: ChartTileComponent.GRID },
          ticks: { color: ChartTileComponent.INK, font: { size: 11 } },
        },
      },
    };
  });
}
